'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import * as Tabs from '@radix-ui/react-tabs';
import { ArrowRight } from 'lucide-react';

/* ==================================================================
   Product previews — inline SVG mockups, one per tab.
   Placeholders that hint at each product's domain. Swap with real
   product screenshots when available.
   ================================================================== */

function IpreferAirPreview() {
  /* Real product hero screenshot. Drop the image at
     /public/new-logos/ipreferair.png and it'll render here.
     Hovering the image scales it slightly inside the card's
     overflow-hidden frame for a subtle "preview-into-product" feel. */
  return (
    <Image
      src="/new-logos/ipreferair.png"
      alt="IpreferAir product preview"
      fill
      sizes="(max-width: 768px) 100vw, 50vw"
      className="object-cover transition-transform duration-500 ease-out hover:scale-105"
      draggable={false}
    />
  );
}

function DebtpulsePreview() {
  return (
    <svg viewBox="0 0 480 360" className="h-full w-full" aria-hidden>
      <defs>
        <linearGradient id="dp-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a1b3a" />
          <stop offset="100%" stopColor="#04080f" />
        </linearGradient>
        <linearGradient id="dp-chart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#058BF0" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#058BF0" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="480" height="360" fill="url(#dp-bg)" rx="12" />

      {/* Top — total balance */}
      <rect x="20" y="20" width="440" height="86" rx="10" fill="#ffffff" opacity="0.05" />
      <text x="36" y="42" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">TOTAL OUTSTANDING</text>
      <text x="36" y="78" fontFamily="system-ui" fontSize="28" fontWeight="800" fill="#ffffff">$24,318</text>
      <text x="180" y="78" fontFamily="system-ui" fontSize="11" fontWeight="700" fill="#34d399">-$612 this month</text>
      {/* Mini sparkline */}
      <path d="M 280 80 L 300 70 L 320 75 L 340 60 L 360 65 L 380 50 L 400 55 L 420 40 L 440 45" stroke="#5BB8FF" strokeWidth="1.6" fill="none" />

      {/* Debt rows */}
      <text x="20" y="128" fontFamily="system-ui" fontSize="10" fontWeight="700" fill="#5BB8FF">YOUR DEBTS</text>
      {[
        { y: 140, name: 'Credit card · Chase', amt: '$8,420', pct: 0.62 },
        { y: 188, name: 'Auto loan', amt: '$11,200', pct: 0.34 },
        { y: 236, name: 'Student loan', amt: '$4,698', pct: 0.81 },
      ].map(({ y, name, amt, pct }, i) => (
        <g key={i}>
          <rect x="20" y={y} width="440" height="40" rx="8" fill="#ffffff" opacity="0.05" />
          <text x="36" y={y + 17} fontFamily="system-ui" fontSize="11" fontWeight="700" fill="#ffffff">{name}</text>
          <text x="36" y={y + 32} fontFamily="system-ui" fontSize="9" fill="#ffffff" opacity="0.55">paid {Math.round(pct * 100)}%</text>
          <rect x="240" y={y + 17} width="180" height="6" rx="3" fill="#ffffff" opacity="0.08" />
          <rect x="240" y={y + 17} width={180 * pct} height="6" rx="3" fill="#058BF0" />
          <text x="446" y={y + 26} textAnchor="end" fontFamily="system-ui" fontSize="12" fontWeight="800" fill="#ffffff">{amt}</text>
        </g>
      ))}

      {/* Payoff trajectory */}
      <rect x="20" y="288" width="440" height="52" rx="8" fill="#ffffff" opacity="0.05" />
      <text x="36" y="306" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">PROJECTED PAYOFF</text>
      <text x="36" y="328" fontFamily="system-ui" fontSize="14" fontWeight="800" fill="#ffffff">Mar 2029</text>
      <path d="M 200 326 L 226 318 L 252 322 L 278 310 L 304 314 L 330 300 L 356 304 L 382 288 L 408 292 L 434 278" stroke="#058BF0" strokeWidth="1.6" fill="none" />
      <path d="M 200 326 L 226 318 L 252 322 L 278 310 L 304 314 L 330 300 L 356 304 L 382 288 L 408 292 L 434 278 L 434 336 L 200 336 Z" fill="url(#dp-chart)" />
    </svg>
  );
}

function SecureflowPreview() {
  return (
    <svg viewBox="0 0 480 360" className="h-full w-full" aria-hidden>
      <defs>
        <linearGradient id="sf-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a1b3a" />
          <stop offset="100%" stopColor="#04080f" />
        </linearGradient>
      </defs>
      <rect width="480" height="360" fill="url(#sf-bg)" rx="12" />

      {/* Header status row */}
      <text x="20" y="36" fontFamily="system-ui" fontSize="11" fontWeight="700" fill="#5BB8FF">POLICY STATUS</text>
      <rect x="430" y="22" width="32" height="14" rx="7" fill="#10b981" opacity="0.22" />
      <text x="446" y="32" textAnchor="middle" fontFamily="system-ui" fontSize="8" fontWeight="800" fill="#34d399">OK</text>

      {/* Policy posture stats */}
      <g>
        <rect x="20" y="56" width="140" height="68" rx="10" fill="#ffffff" opacity="0.05" />
        <text x="34" y="76" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">POLICIES</text>
        <text x="34" y="104" fontFamily="system-ui" fontSize="22" fontWeight="800" fill="#ffffff">128</text>
        <text x="80" y="104" fontFamily="system-ui" fontSize="10" fill="#34d399">+4</text>
      </g>
      <g>
        <rect x="170" y="56" width="140" height="68" rx="10" fill="#ffffff" opacity="0.05" />
        <text x="184" y="76" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">VIOLATIONS</text>
        <text x="184" y="104" fontFamily="system-ui" fontSize="22" fontWeight="800" fill="#ffffff">3</text>
        <text x="218" y="104" fontFamily="system-ui" fontSize="10" fill="#f87171">live</text>
      </g>
      <g>
        <rect x="320" y="56" width="140" height="68" rx="10" fill="#ffffff" opacity="0.05" />
        <text x="334" y="76" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">COVERAGE</text>
        <text x="334" y="104" fontFamily="system-ui" fontSize="22" fontWeight="800" fill="#ffffff">96%</text>
        <rect x="386" y="92" width="60" height="6" rx="3" fill="#ffffff" opacity="0.08" />
        <rect x="386" y="92" width="58" height="6" rx="3" fill="#058BF0" />
      </g>

      {/* Access events timeline */}
      <text x="20" y="148" fontFamily="system-ui" fontSize="10" fontWeight="700" fill="#5BB8FF">RECENT EVENTS</text>
      {[
        { y: 158, who: 'svc-billing', role: 'Read · customers', state: 'allow' },
        { y: 198, who: 'user · alex@', role: 'Export · invoices', state: 'allow' },
        { y: 238, who: 'svc-etl', role: 'Write · warehouse', state: 'deny' },
        { y: 278, who: 'user · sam@', role: 'Read · audit-log', state: 'allow' },
      ].map(({ y, who, role, state }, i) => {
        const dotColor = state === 'allow' ? '#34d399' : '#f87171';
        const pillBg = state === 'allow' ? 'rgba(52,211,153,0.18)' : 'rgba(248,113,113,0.18)';
        const pillText = state === 'allow' ? '#34d399' : '#f87171';
        return (
          <g key={i}>
            <rect x="20" y={y} width="440" height="32" rx="8" fill="#ffffff" opacity="0.05" />
            <circle cx="38" cy={y + 16} r="5" fill={dotColor} />
            <text x="56" y={y + 14} fontFamily="system-ui" fontSize="11" fontWeight="700" fill="#ffffff">{who}</text>
            <text x="56" y={y + 26} fontFamily="system-ui" fontSize="9" fill="#ffffff" opacity="0.55">{role}</text>
            <rect x="400" y={y + 8} width="46" height="16" rx="4" fill={pillBg} />
            <text x="423" y={y + 19} textAnchor="middle" fontFamily="system-ui" fontSize="9" fontWeight="800" fill={pillText}>
              {state.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* Footer */}
      <rect x="20" y="322" width="440" height="22" rx="6" fill="#ffffff" opacity="0.05" />
      <text x="36" y="337" fontFamily="system-ui" fontSize="9" fontWeight="700" fill="#5BB8FF">DATA FLOW</text>
      <text x="100" y="337" fontFamily="system-ui" fontSize="9" fontWeight="600" fill="#ffffff" opacity="0.7">Source → Policy → Sink · 24 active streams</text>
    </svg>
  );
}

/* ==================================================================
   Tab data
   - 3 Cloudastra products. Descriptions use soft language; swap them
     with your real product positioning whenever convenient.
   ================================================================== */

const products = [
  {
    value: 'ipreferair',
    logo: '/new-logos/ipreferair-mark.png',
    label: 'IpreferAir',
    // Active state recolors text + icon (icon is bg-current) to the product
    // brand color, with that color at 20% as the tab background.
    active:
      'data-[state=active]:bg-[#EF3F3E]/10 data-[state=active]:text-[#EF3F3E]',
    badge: 'Travel',
    title: 'Smarter air travel, end to end.',
    description:
      'Search, book, and manage flights through a single experience designed for travelers who value time. Built around the journey, not the booking funnel.',
    cta: 'See IpreferAir',
    href: '/products/ipreferair',
    Preview: IpreferAirPreview,
  },
  {
    value: 'secureflow',
    logo: '/new-logos/secureflow-mark.png',
    label: 'Secureflow',
    active:
      'data-[state=active]:bg-[#16A34A]/10 data-[state=active]:text-[#16A34A]',
    badge: 'Security & Compliance',
    title: 'Move data with confidence.',
    description:
      'Policy-aware data movement with full audit trails. The control plane teams need to ship fast without slipping on security or compliance posture.',
    cta: 'See Secureflow',
    href: '/products/secureflow',
    Preview: SecureflowPreview,
  },
  {
    value: 'debtpulse',
    logo: '/new-logos/debtpulse-mark.png',
    label: 'Debtpulse',
    active:
      'data-[state=active]:bg-[#E63E1C]/10 data-[state=active]:text-[#E63E1C]',
    badge: 'Financial Health',
    title: 'Take the guesswork out of debt.',
    description:
      'Track balances, plan payoffs, and surface the moves that actually shift your number. A clearer signal for anyone serious about getting to zero.',
    cta: 'See Debtpulse',
    href: '/products/debtpulse',
    Preview: DebtpulsePreview,
  },
];

/* ==================================================================
   Section
   ================================================================== */

export default function Products() {
  const [value, setValue] = useState(products[0].value);

  // Phones: the tab row scrolls sideways. Keep the active tab in view as
  // auto-rotation moves it (scroll the row only, never the page).
  const listRef = useRef(null);
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const tab = list.querySelector('[data-state="active"]');
    if (!tab) return;
    const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    list.scrollTo({ left, behavior: 'smooth' });
  }, [value]);
  // Pause auto-rotation while the user is hovering the tabs so it doesn't
  // jump away mid-read.
  const pausedRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setValue(prev => {
        const i = products.findIndex(p => p.value === prev);
        return products[(i + 1) % products.length].value;
      });
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-nav-theme="light"
      className="relative bg-[#f6f8fb] py-24 lg:py-32"
    >
      <div className="mx-auto w-11/12 max-w-[1280px]">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#058BF0] sm:text-sm">
            Our Products
          </p>
          <h2 className="mt-5 font-poppins text-3xl font-bold leading-[1.2] sm:leading-[1.2] lg:leading-[1.2] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.25rem]">
            Products built to ship. Engineered to scale.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-gray-600 lg:text-lg">
            Each one solves a real bottleneck for our customers, built with the same AI-first engineering standards we bring to every Cloudastra product.
          </p>
        </div>

        {/* Tabs */}
        <Tabs.Root
          value={value}
          onValueChange={setValue}
          className="mt-12"
          onMouseEnter={() => {
            pausedRef.current = true;
          }}
          onMouseLeave={() => {
            pausedRef.current = false;
          }}
        >
          {/* Tab triggers + "View all" action button.
              The link is a plain <a> (not a Tabs.Trigger), so it navigates
              instead of switching tabs. It still lives inside Tabs.List so
              the row visually reads as a single segmented control. */}
          <Tabs.List
            ref={listRef}
            aria-label="Cloudastra products"
            className="relative mx-auto flex w-full max-w-4xl snap-x snap-mandatory items-stretch gap-1.5 overflow-x-auto rounded-2xl border border-gray-200/80 bg-white p-1.5 shadow-sm [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:snap-none sm:gap-2 sm:overflow-visible"
          >
            {products.map(({ value, logo, label, active }) => (
              <Tabs.Trigger
                key={value}
                value={value}
                className={`group flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-semibold text-gray-500 transition-colors hover:text-[#0a1b3a] sm:flex-1 sm:shrink ${active}`}
              >
                {/* Logo mark recolored to match the label (currentColor) via mask. */}
                <span
                  aria-hidden
                  className="h-5 w-5 shrink-0 bg-current"
                  style={{
                    WebkitMaskImage: `url(${logo})`,
                    maskImage: `url(${logo})`,
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                  }}
                />
                {label}
              </Tabs.Trigger>
            ))}

            {/* View all — looks like a tab but navigates */}
            <a
              href="/products"
              className="group flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-transparent px-4 py-3 text-sm font-semibold text-gray-500 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-[#0a1b3a] sm:flex-1 sm:shrink"
            >
              View all
              <ArrowRight
                className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.2}
              />
            </a>
          </Tabs.List>

          {/* Tab content panes */}
          {products.map(
            ({ value, badge, title, description, cta, href, Preview }) => (
              <Tabs.Content
                key={value}
                value={value}
                className="mt-10 focus:outline-none data-[state=inactive]:hidden"
              >
                <div className="grid items-center gap-10 rounded-3xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(10,27,58,0.04)] lg:grid-cols-[5fr_7fr] lg:gap-12 lg:p-10">
                  {/* Left — copy */}
                  <div className="flex flex-col gap-5">
                    <span className="inline-flex w-fit items-center rounded-full border border-[#058BF0]/20 bg-[#058BF0]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#058BF0]">
                      {badge}
                    </span>
                    <h3 className="font-poppins text-2xl font-extrabold leading-tight text-[#0a1b3a] lg:text-3xl xl:text-[2.25rem]">
                      {title}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-600 lg:text-base">
                      {description}
                    </p>
                    <a
                      href={href}
                      className="group mt-1 inline-flex w-fit items-center gap-2 rounded-lg bg-[#058BF0] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-cloud/30 transition hover:bg-[#0477cc]"
                    >
                      {cta}
                      <ArrowRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        strokeWidth={2.2}
                      />
                    </a>
                  </div>

                  {/* Right — product preview */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#0a1b3a] shadow-[0_24px_60px_-30px_rgba(5,139,240,0.45)]">
                    <Preview />
                  </div>
                </div>
              </Tabs.Content>
            )
          )}
        </Tabs.Root>
      </div>
    </section>
  );
}
